/* eslint-disable no-console */
import {isError} from 'lodash';

import store from '~/shared/store';
import {actions as errorDialogDataActions} from '~/shared/store/storeModules/errorDialogData';

import {init as datadogInit, reportError} from './datadog';
import {initialPrints} from './consoleFilterMessage';

const IS_PRODUCTION = process.env.NODE_ENV === 'production' && process.env.ENVIRONMENT === 'production';

/**
 * Optional extras object that can be passed as the last argument to `logger.error`.
 *
 * Set `sendReport: true` to opt this call in to Datadog Error Tracking. Without it, the
 * error is written to the console only and never leaves the browser.
 * Only pass `sendReport: true` for genuinely unexpected, unrecoverable errors that are
 * not already handled elsewhere in the call stack.
 */
export interface LoggerErrorExtras {
  sendReport?: boolean;
  fingerprint?: string;
  [key: string]: unknown;
}

export function initLogging() {
  initialPrints();
  datadogInit();
}

enum LogLevels {
  VERBOSE = 'verbose',
  LOG = 'log',
  WARN = 'warn',
  ERROR = 'error',
  ERROR_DIALOG = 'errorDialog',
}

const loggerLevelToConsoleLevel = {
  [LogLevels.VERBOSE]: 'info',
  [LogLevels.ERROR_DIALOG]: LogLevels.ERROR,
} as const;

const loggerLevelsToReport = {
  [LogLevels.ERROR]: true,
  [LogLevels.ERROR_DIALOG]: true,
};

const allowConsoleLogs = !IS_PRODUCTION || localStorage?.allowConsoleLogs === 'true';

function isErrorLevel(logLevel: LogLevels): logLevel is keyof typeof loggerLevelsToReport {
  return Object.keys(loggerLevelsToReport).includes(logLevel);
}

function isNotConsoleLevel(logLevel: LogLevels): logLevel is keyof typeof loggerLevelToConsoleLevel {
  return Object.keys(loggerLevelToConsoleLevel).includes(logLevel);
}

/**
 * The single gate between the application and Datadog. Everything that is not opted in
 * with `{sendReport: true}` stays in the browser console and is never sent anywhere.
 */
function trackError({
  loggerName,
  errorOrMessage,
  args,
}: {
  loggerName: string;
  errorOrMessage: string | Error;
  args: any;
}) {
  try {
    if (!args?.some((item: any) => item?.sendReport === true)) {
      return;
    }

    // Expired sessions are expected and handled by the auth layer.
    if (args.some((item: any) => item?.status === 401)) {
      return;
    }

    const error = isError(errorOrMessage) ? errorOrMessage : new Error(errorOrMessage);

    reportError({loggerName, error, args});
  } catch (err) {
    console.error(new Error('trackError in logging errored'), {error: err});
  }
}

function loggerFn(logLevel: LogLevels, loggerName: string, errorOrMessage: string | Error, ...args: any) {
  try {
    if (isErrorLevel(logLevel)) {
      trackError({loggerName, errorOrMessage, args});
    }
    if (allowConsoleLogs) {
      const consoleLevel = isNotConsoleLevel(logLevel)
        ? (loggerLevelToConsoleLevel[
            logLevel
          ] as typeof loggerLevelToConsoleLevel[keyof typeof loggerLevelToConsoleLevel])
        : logLevel;
      console[consoleLevel](`$[${loggerName}]: ${errorOrMessage}`, ...args);
    }
  } catch (e) {
    console.error(new Error('Failed to log an error using the logger.'), {logLevel, loggerName, errorOrMessage, args});
  }
}

export function createLogger(loggerName: string) {
  // repeating code for the sake of intellisense
  return {
    [LogLevels.VERBOSE]: (errorOrMessage: string | Error, ...args: any) =>
      loggerFn(LogLevels.VERBOSE, loggerName, errorOrMessage, ...args),

    [LogLevels.LOG]: (errorOrMessage: string | Error, ...args: any) =>
      loggerFn(LogLevels.LOG, loggerName, errorOrMessage, ...args),

    [LogLevels.WARN]: (errorOrMessage: string | Error, ...args: any) =>
      loggerFn(LogLevels.WARN, loggerName, errorOrMessage, ...args),

    // `args` stays `any` for backwards compatibility (callers pass caught errors,
    // strings, etc. as extras). Pass a `LoggerErrorExtras` object to opt in to a
    // Datadog report, e.g. `logger.error('msg', {error, sendReport: true})`.
    [LogLevels.ERROR]: (errorOrMessage: string | Error, ...args: any) =>
      loggerFn(LogLevels.ERROR, loggerName, errorOrMessage, ...args),

    [LogLevels.ERROR_DIALOG]: (errorOrMessage: string | Error, ...args: any) => {
      if (!IS_PRODUCTION) {
        store.dispatch(errorDialogDataActions.addErrorsToDialog({errorOrMessage, args}));
      }

      return loggerFn(LogLevels.ERROR_DIALOG, loggerName, errorOrMessage, ...args);
    },
  };
}
